/**
 * Mobile Price Update Handler
 * Updates mobile product price display when variants change
 *
 * @param {string} sectionId - The section ID from Shopify
 * @param {string} productId - The product ID from Shopify
 * @param {string} currency - The shop currency code
 * @param {string} moneyFormat - The shop money format string
 * @param {string|null} discountBadgeImageUrl - Effective discount badge image URL, or null to use text fallback
 * @param {string} discountBadgeTextColor - Discount badge text color
 */
(function(window) {
  'use strict';

  function initMobilePriceUpdate(sectionId, productId, currency, moneyFormat, discountBadgeImageUrl, discountBadgeTextColor) {
    function buildDiscountBadgeHtml(discountPercentage) {
      if (discountBadgeImageUrl) {
        return `<div class="price__discount-badge price__discount-badge--large"><img src="${discountBadgeImageUrl}" alt="" class="price__discount-badge-image" loading="lazy"><span class="price__discount-badge-text" style="color: ${discountBadgeTextColor};"><span class="price__discount-badge-label">Save</span><span class="price__discount-badge-percent">${discountPercentage}%</span></span></div>`;
      }
      return `<span class="price__discount-percentage" style="color: rgb(var(--color-sale-price)); font-weight: normal; font-size:35px; line-height: 1;">-${discountPercentage}%</span>`;
    }

    function updateMobilePrice(variant) {
      if (!variant) return;

      const priceInCents = variant.price; // Shopify prices are in cents
      const compareAtPriceInCents = variant.compare_at_price;
      const price = (priceInCents / 100).toFixed(2); // Convert to dollars with 2 decimal places
      const compareAtPrice = (compareAtPriceInCents / 100).toFixed(2);
      const dividedPrice = (priceInCents / 4 / 100).toFixed(2);
      const currencySymbol = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : currency;
      const useCommaSeparator = moneyFormat.includes('amount_with_comma_separator');

      const isOnSale = compareAtPriceInCents && parseFloat(price) < parseFloat(compareAtPrice);
      let discountPercentage = '';
      if (isOnSale) {
        const discountAmount = parseFloat(compareAtPrice) - parseFloat(price);
        discountPercentage = Math.round((discountAmount / parseFloat(compareAtPrice)) * 100);
      }

      const mobilePriceElement = document.getElementById(`mobile-price-${sectionId}-${productId}`);
      const discountPriceElement = document.getElementById(`discount-price-${sectionId}-${productId}`);
      const mobileDiscountPriceElement = document.getElementById(`mobile-discount-price-${sectionId}-${productId}`);

      if (!mobilePriceElement) return;

      const discountBadgeHtml = isOnSale ? buildDiscountBadgeHtml(discountPercentage) : '';

      if (useCommaSeparator) {
        const formattedDividedPrice = moneyFormat.replace(/\{\{amount_with_comma_separator\}\}/g, dividedPrice).replace(".", ",");
        const formattedPrice = moneyFormat.replace(/\{\{amount_with_comma_separator\}\}/g, price).replace(".", ",") + ' ' + currencySymbol;
        const formattedCompareAtPrice = moneyFormat.replace(/\{\{amount_with_comma_separator\}\}/g, compareAtPrice).replace(".", ",") + ' ' + currencySymbol;

        if (discountPriceElement) discountPriceElement.innerText = formattedDividedPrice;
        if (mobileDiscountPriceElement) mobileDiscountPriceElement.innerText = formattedDividedPrice;

        if(isOnSale) {
          mobilePriceElement.innerHTML = `<div class="price price--on-sale flex flex-wrap items-baseline gap-2 lg:flex-col lg:gap-1d5"><span class="sr-only">Sale price</span><div class="price__container price__container--on-sale flex items-center gap-2">${discountBadgeHtml}<div class="price__prices flex flex-col"><span class="price__regular price__regular--sale whitespace-nowrap" style="font-size:35px; color: rgb(var(--color-foreground));">${formattedPrice}</span><span class="sr-only">Regular price</span><span class="price__sale inline-flex items-center h-auto"><span class="price__sale-text relative">${formattedCompareAtPrice}</span></span></div></div></div>`;
        } else {
          mobilePriceElement.innerHTML = `<div class="price flex flex-wrap items-baseline gap-2 lg:flex-col lg:gap-1d5"><span class="price__regular whitespace-nowrap" style="font-size:35px;">${formattedPrice}</span></div>`;
        }
      } else {
        const formattedDividedPrice = moneyFormat.replace(/\{\{amount\}\}/g, dividedPrice);
        const formattedPrice = moneyFormat.replace(/\{\{amount\}\}/g, price) + ' ' + currencySymbol;
        const formattedCompareAtPrice = moneyFormat.replace(/\{\{amount\}\}/g, compareAtPrice) + ' ' + currencySymbol;

        if (discountPriceElement) discountPriceElement.innerText = formattedDividedPrice;
        if (mobileDiscountPriceElement) mobileDiscountPriceElement.innerText = formattedDividedPrice;

        if(isOnSale) {
          mobilePriceElement.innerHTML = `<div class="price price--on-sale flex flex-wrap items-baseline gap-2 lg:flex-col lg:gap-1d5"><span class="sr-only">Sale price</span><div class="price__container price__container--on-sale flex items-center gap-2">${discountBadgeHtml}<div class="price__prices flex flex-col"><span class="price__regular price__regular--sale whitespace-nowrap" style="font-size:35px; color: rgb(var(--color-foreground));">${formattedPrice}</span><span class="sr-only">Regular price</span><span class="price__sale inline-flex items-center h-auto"><span class="price__sale-text relative">${formattedCompareAtPrice}</span></span></div></div></div>`;
        } else {
          mobilePriceElement.innerHTML = `<div class="price flex flex-wrap items-baseline gap-2 lg:flex-col lg:gap-1d5"><span class="price__regular whitespace-nowrap" style="font-size:35px;">${formattedPrice}</span></div>`;
        }
      }
    }

    function setupVariantListener() {
      const form = document.querySelector("form[action$='/cart/add']");
      if (!form) {
        // Retry after a short delay if form not found
        setTimeout(setupVariantListener, 100);
        return;
      }

      const variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) {
        // Retry after a short delay if input not found
        setTimeout(setupVariantListener, 100);
        return;
      }

      // Listen to the hidden input change event
      variantInput.addEventListener("change", function(event) {
        const selectedVariantId = event.target.value;

        if (!selectedVariantId) return;

        fetch(`${window.location.pathname}.js`)
          .then(response => response.json())
          .then(data => {
            const variant = data.variants.find(v => v.id == selectedVariantId);
            updateMobilePrice(variant);
          })
          .catch(err => console.error('Error updating mobile price:', err));
      });
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupVariantListener);
    } else {
      setupVariantListener();
    }

    // Also listen to theme's pubsub variantChange event as backup
    if (typeof window.theme !== 'undefined' && window.theme.pubsub) {
      window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data && event.data.sectionId === sectionId && event.data.productId === productId && event.data.variant) {
          updateMobilePrice(event.data.variant);
        }
      });
    }
  }

  // Export to window for global access
  window.initMobilePriceUpdate = initMobilePriceUpdate;
})(window);
